import { useQuery, useQueryClient } from '@tanstack/react-query';
import { postsApi, CalendarData, Post } from '@/services/posts';
import { calendarService } from '@/services/calendar.service';
import { startOfMonth, endOfMonth } from 'date-fns';
import { useTimezone } from '@/contexts/TimezoneContext';
import { teamQueryKeys } from '@/lib/queryKeys';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';

/**
 * React Query hook for calendar posts
 *
 * Fetches posts grouped by date for the calendar view.
 * Implements circuit breaker pattern to prevent infinite retries.
 */

interface UseCalendarPostsOptions {
  /**
   * Month to fetch in YYYY-MM format
   */
  month: string;

  /**
   * Optional platform filter
   */
  platform?: string;

  /**
   * Whether to enable the query
   * @default true
   */
  enabled?: boolean;
}

interface CalendarPostsResult {
  /**
   * Calendar data: posts grouped by date
   */
  data: CalendarData | undefined;

  /**
   * Loading state
   */
  isLoading: boolean;

  /**
   * Error state
   */
  isError: boolean;

  /**
   * Error message
   */
  error: Error | null;

  /**
   * Refetch function
   */
  refetch: () => void;

  /**
   * Get posts for a specific date
   */
  getPostsForDate: (date: string) => Post[];

  /**
   * Get post count for a specific date
   */
  getPostCountForDate: (date: string) => number;
}

/**
 * Custom hook for fetching calendar posts
 *
 * @param options - Hook options
 * @returns CalendarPostsResult
 *
 * @example
 * ```tsx
 * const { data, isLoading, getPostsForDate } = useCalendarPosts({
 *   month: '2025-11',
 *   platform: 'LINKEDIN'
 * });
 * ```
 */
export const useCalendarPosts = ({
  month,
  platform,
  enabled = true,
}: UseCalendarPostsOptions): CalendarPostsResult => {
  const queryClient = useQueryClient();
  const { teamId, hasTeam } = useEffectiveTeamId();
  const { timezone } = useTimezone();

  // Parse month string (YYYY-MM) into year and month numbers
  const [year, monthNum] = month ? month.split('-').map(Number) : [0, 0];

  const query = useQuery({
    queryKey: teamQueryKeys.calendarPosts.monthly(teamId, year, monthNum),
    queryFn: () => postsApi.getCalendar(month, platform, timezone),
    enabled: hasTeam && enabled && !!month,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2, // Only retry twice to prevent rate limiting
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  /**
   * Get posts for a specific date
   */
  const getPostsForDate = (date: string): Post[] => {
    if (!query.data) return [];
    return query.data[date] || [];
  };

  /**
   * Get post count for a specific date
   */
  const getPostCountForDate = (date: string): number => {
    return getPostsForDate(date).length;
  };

  /**
   * Refetch with cache invalidation
   */
  const refetch = () => {
    const [year, monthNum] = month ? month.split('-').map(Number) : [0, 0];
    queryClient.invalidateQueries({ queryKey: teamQueryKeys.calendarPosts.monthly(teamId, year, monthNum) });
  };

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch,
    getPostsForDate,
    getPostCountForDate,
  };
};

/**
 * Prefetch calendar data for adjacent months
 * Useful for smoother navigation experience
 */
export const usePrefetchAdjacentMonths = (currentMonth: string) => {
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();
  const { timezone } = useTimezone();

  const prefetch = () => {
    const [year, month] = currentMonth.split('-').map(Number);

    // Previous month
    const prevDate = new Date(year, month - 2, 1);
    const prevYear = prevDate.getFullYear();
    const prevMonth = prevDate.getMonth() + 1;

    // Next month
    const nextDate = new Date(year, month, 1);
    const nextYear = nextDate.getFullYear();
    const nextMonth = nextDate.getMonth() + 1;

    // Prefetch both months with team-scoped keys
    queryClient.prefetchQuery({
      queryKey: teamQueryKeys.calendarPosts.monthly(teamId, prevYear, prevMonth),
      queryFn: () => postsApi.getCalendar(`${prevYear}-${String(prevMonth).padStart(2, '0')}`, undefined, timezone),
      staleTime: 5 * 60 * 1000,
    });

    queryClient.prefetchQuery({
      queryKey: teamQueryKeys.calendarPosts.monthly(teamId, nextYear, nextMonth),
      queryFn: () => postsApi.getCalendar(`${nextYear}-${String(nextMonth).padStart(2, '0')}`, undefined, timezone),
      staleTime: 5 * 60 * 1000,
    });
  };

  return { prefetch };
};

/**
 * React Query hook for multi-calendar posts
 *
 * Fetches posts from multiple calendars and merges them by date.
 * If no calendar IDs provided, falls back to regular calendar API.
 */
interface UseMultiCalendarPostsOptions {
  /**
   * Month to fetch in YYYY-MM format
   */
  month: string;

  /**
   * Calendar IDs to fetch from
   */
  calendarIds: number[];

  /**
   * Optional platform filter
   */
  platform?: string;

  /**
   * Whether to enable the query
   * @default true
   */
  enabled?: boolean;
}

export const useMultiCalendarPosts = ({
  month,
  calendarIds,
  platform,
  enabled = true,
}: UseMultiCalendarPostsOptions): CalendarPostsResult => {
  const queryClient = useQueryClient();
  const { formatDateOnly, timezone } = useTimezone();
  const { teamId, hasTeam } = useEffectiveTeamId();

  // Parse month to get date range
  const [year, monthNum] = month.split('-').map(Number);
  const monthDate = new Date(year, monthNum - 1, 1);
  const startDate = startOfMonth(monthDate);
  const endDate = endOfMonth(monthDate);

  // Helper to get date key in user's timezone (yyyy-MM-dd format)
  const getDateKey = (dateStr: string | Date): string => {
    // formatDateOnly returns 'dd.MM.yyyy', we need 'yyyy-MM-dd' for keys
    const formatted = formatDateOnly(dateStr);
    if (!formatted) return '';
    // Convert from dd.MM.yyyy to yyyy-MM-dd
    const [day, monthPart, yearPart] = formatted.split('.');
    return `${yearPart}-${monthPart}-${day}`;
  };

  // Create params object for query key
  const params = {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    platform: platform && platform !== 'ALL' ? platform : undefined,
  };

  const query = useQuery({
    queryKey: teamQueryKeys.calendarPosts.multiCalendar(teamId, calendarIds, params),
    queryFn: async (): Promise<CalendarData> => {
      // If no calendars selected, return empty data
      if (calendarIds.length === 0) {
        return {};
      }

      // Fetch posts from each calendar
      const postsPromises = calendarIds.map((calendarId) =>
        calendarService.getCalendarPosts(calendarId, {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          platform: platform && platform !== 'ALL' ? platform : undefined,
          timezone,
        })
      );

      const allPostsArrays = await Promise.all(postsPromises);

      // Merge posts by date (using user's timezone)
      const mergedData: CalendarData = {};

      allPostsArrays.forEach((posts) => {
        posts.forEach((post) => {
          // Get the date in user's timezone
          const dateStr = post.scheduledAt
            ? getDateKey(post.scheduledAt)
            : getDateKey(post.createdAt);

          if (!mergedData[dateStr]) {
            mergedData[dateStr] = [];
          }

          mergedData[dateStr].push(post);
        });
      });

      // Sort posts within each date by scheduledAt
      Object.keys(mergedData).forEach((date) => {
        mergedData[date].sort((a, b) => {
          const aTime = a.scheduledAt ? new Date(a.scheduledAt).getTime() : 0;
          const bTime = b.scheduledAt ? new Date(b.scheduledAt).getTime() : 0;
          return aTime - bTime;
        });
      });

      return mergedData;
    },
    enabled: hasTeam && enabled && !!month && calendarIds.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  /**
   * Get posts for a specific date
   */
  const getPostsForDate = (date: string): Post[] => {
    if (!query.data) return [];
    return query.data[date] || [];
  };

  /**
   * Get post count for a specific date
   */
  const getPostCountForDate = (date: string): number => {
    return getPostsForDate(date).length;
  };

  /**
   * Refetch with cache invalidation
   */
  const refetch = () => {
    queryClient.invalidateQueries({
      queryKey: teamQueryKeys.calendarPosts.multiCalendar(teamId, calendarIds, params)
    });
  };

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch,
    getPostsForDate,
    getPostCountForDate,
  };
};
